// The evenings a game's busy_hours tag can name, in one place for everything
// that reads them: the tagger (../../tagger), which tags each game by them,
// and the site (chart-panel.ts, game.ts, app.js), which names and shades them. Each is EVENING_HOURS
// of local time from `from` in `zone`, in the order a tag names them, read in
// that zone's own time so it follows daylight saving.

import type { AppTag } from "./api.ts";
import { numberOrNull } from "./guards.ts";

// A region's evening: how a tag names it, its IANA zone, and the local hour
// it starts.
export interface Evening {
  label: string;
  place: string;
  zone: string;
  from: number;
}

export const EVENINGS = {
  americas: { label: "Americas", place: "the Americas", zone: "America/New_York", from: 20 },
  europe: { label: "Europe", place: "Europe", zone: "Europe/Berlin", from: 19 },
  east_asia: { label: "East Asia", place: "East Asia", zone: "Asia/Shanghai", from: 20 }
} satisfies Record<string, Evening>;

// A region whose evening busy_hours can name: a key of EVENINGS.
export type Region = keyof typeof EVENINGS;

export const isRegion = (name: string): name is Region => Object.hasOwn(EVENINGS, name);

// The regions, in EVENINGS order, which is the order a tag names them.
export const REGIONS: readonly Region[] = /* @__PURE__ */ Object.keys(EVENINGS).filter(isRegion);

export const EVENING_HOURS = 4;

// The zone a busy_hours tag names for a day with no evening peak.
export const AROUND_THE_CLOCK = "around_the_clock";

// When a game is played, from its busy_hours tag (GET /api/apps/:id's
// `tags`, value { zones, peakHour }): `evenings`, its zones in EVENINGS
// order, [] for AROUND_THE_CLOCK; or null for no tag, or zones that are all
// regions this copy doesn't know (one the tagger added since), which says
// nothing rather than "around the clock". And `peakHour`, the busiest UTC
// hour, or null (always, around the clock).
export function playTimes(tags: readonly Pick<AppTag, "tag" | "value">[] = []): {
  evenings: Region[] | null;
  peakHour: number | null;
} {
  const period = tags.find((t) => t.tag === "busy_hours")?.value;
  // A tag's value is the API's JSON, so its fields are checked.
  const zones: unknown[] = Array.isArray(period?.zones) ? period.zones : [];
  const evenings = REGIONS.filter((region) => zones.includes(region));
  return {
    evenings:
      zones.includes(AROUND_THE_CLOCK) ? [] : evenings.length ? evenings : null,
    peakHour: numberOrNull(period?.peakHour)
  };
}

const HOUR_MS = 3600000;

// How far `zone` is ahead of UTC at `ms` (unix ms), in ms, from what its clock
// reads then.
const zoneFormats = new Map<string, Intl.DateTimeFormat>();

export function zoneOffset(zone: string, ms: number): number {
  let format = zoneFormats.get(zone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric"
    });
    zoneFormats.set(zone, format);
  }

  // One pass over the parts. The format's options ask for each of these, so
  // each is there.
  const part = Object.fromEntries(
    format.formatToParts(ms).map(({ type, value }) => [type, Number(value)])
  ) as Record<"year" | "month" | "day" | "hour" | "minute", number>;
  const wall = Date.UTC(part.year, part.month - 1, part.day, part.hour, part.minute);

  return wall - Math.floor(ms / 60000) * 60000;
}

// The unix time (seconds) `zone`'s clocks read `hour`:00 on the date of UTC
// day `day` (unix seconds): the wall time less the zone's offset then, which
// is checked again at the result in case daylight saving changed in between.
export function zonedHour(zone: string, day: number, hour: number): number {
  const wall = day * 1000 + hour * HOUR_MS;
  const guess = wall - zoneOffset(zone, wall);

  return (wall - zoneOffset(zone, guess)) / 1000;
}

// A region's evening as the UTC hours of the day (0–23) it covers at `ms`
// (unix ms), by its zone's offset then: what the tagger averages, so a run
// in winter uses winter's hours.
export function eveningUtcHours(region: Region, ms: number): number[] {
  const { zone, from } = EVENINGS[region];
  const offset = Math.round(zoneOffset(zone, ms) / HOUR_MS);

  return Array.from({ length: EVENING_HOURS }, (_, i) => (((from + i - offset) % 24) + 24) % 24);
}
